import React, { useState, createContext, useContext } from 'react';

// Context for tabs
const TabsContext = createContext();

// Main Tabs container
export function Tabs({ 
  value, 
  onChange, 
  children, 
  variant = 'standard', // 'standard', 'fullWidth'
  className = '',
  ...props 
}) {
  const [activeTab, setActiveTab] = useState(value ?? 0);
  
  const handleChange = (newValue) => {
    setActiveTab(newValue);
    onChange?.(null, newValue); // Match component API standard
  };

  const tabsClasses = `
    border-b border-gray-300 border-opacity-20
    ${variant === 'fullWidth' ? 'w-full' : ''}
    ${className}
  `;

  return (
    <TabsContext.Provider value={{ activeTab: value ?? activeTab, handleChange }}>
      <div className={tabsClasses} {...props}>
        <div className={`flex ${variant === 'fullWidth' ? 'w-full' : ''}`}>
          {children}
        </div>
      </div>
    </TabsContext.Provider>
  );
}

// Individual Tab component
export function Tab({ 
  label, 
  value, 
  disabled = false, 
  className = '',
  ...props 
}) {
  const { activeTab, handleChange } = useContext(TabsContext);
  const isActive = activeTab === value;

  const handleClick = () => {
    if (!disabled) {
      handleChange(value);
    }
  };

  const tabClasses = `
    relative px-6 py-3 text-sm font-medium transition-all duration-200 ease-in-out cursor-pointer
    select-none border-b-2 border-transparent
    ${isActive 
      ? 'text-purple-300 border-purple-400' 
      : 'text-gray-400 hover:text-gray-200 hover:border-gray-300 hover:border-opacity-30'
    }
    ${disabled 
      ? 'opacity-50 cursor-not-allowed' 
      : ''
    }
    ${className}
  `;

  return (
    <button
      className={tabClasses}
      onClick={handleClick}
      disabled={disabled}
      type="button"
      {...props}
    >
      {label}
      
      {/* Active indicator */}
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-400 rounded-full" />
      )}
    </button>
  );
}

// TabPanel component for content
export function TabPanel({ 
  children, 
  value, 
  activeValue, 
  className = '',
  ...props 
}) {
  const isVisible = value === activeValue;

  if (!isVisible) return null;

  const panelClasses = `
    py-6 text-white
    ${className}
  `;

  return (
    <div 
      className={panelClasses}
      role="tabpanel"
      hidden={!isVisible}
      {...props}
    >
      {children}
    </div>
  );
}

// Helper component that matches standard tab pattern
export function TabContext({ value, children }) {
  return (
    <TabsContext.Provider value={{ activeTab: value }}>
      {children}
    </TabsContext.Provider>
  );
} 